/**
 * Provider-agnostic customization cart integration.
 *
 * Personalization providers (Mediaclip today; others tomorrow) add each
 * customized product to the cart as a *main* line plus one or more hidden
 * *sub-part* lines (e.g. a photobook's "Feuille supp." / extra sheets), all
 * grouped by a shared id. Two things must happen that neither Shopify's native
 * cart nor this theme's custom cart do on their own:
 *
 *   1. When the shopper changes the MAIN line quantity, the sub-parts must scale
 *      with it. Otherwise a customer can order 3 albums but pay for the extra
 *      pages of only 1 — exactly the under-charging Mediaclip flagged.
 *   2. The cart must be validated before checkout so quantity mismatches are
 *      fixed client-side (one click) instead of being rejected server-side.
 *
 * This module hooks the theme's existing cart WITHOUT taking it over:
 *   - It intercepts quantity changes only on customization MAIN lines and
 *     delegates the sync to the matching provider adapter, then lets the theme
 *     re-render as usual (theme:cart:refresh).
 *   - It intercepts checkout to run each present provider's validation.
 * Plain products, and providers we don't recognize, are left entirely to the
 * theme's native behaviour.
 *
 * Adding a provider = adding one adapter to PROVIDERS. The DOM contract emitted
 * by snippets/cart-line-items-tipii.liquid is:
 *   .cart__item[data-customization-provider][data-customization-group][data-customization-main]
 * and the checkout buttons carry [data-customization-checkout].
 *
 * Defence in depth: even if this script never runs (e.g. the provider's app
 * embed is disabled), the cart <form> posts to the provider's server endpoint
 * (/tools/customize/handle-cart-form for Mediaclip) which re-validates and
 * blocks bad orders. This client layer just turns that hard block into a
 * friendly "click to fix" flow.
 */
(function () {
  'use strict';

  // How long to wait for a provider's JS API to appear before giving up and
  // falling back to the server-side validation backstop.
  var API_TIMEOUT_MS = 8000;

  function waitFor(predicate, timeout) {
    return new Promise(function (resolve) {
      if (predicate()) return resolve(true);
      var start = Date.now();
      var timer = setInterval(function () {
        if (predicate()) {
          clearInterval(timer);
          resolve(true);
        } else if (Date.now() - start > (timeout || API_TIMEOUT_MS)) {
          clearInterval(timer);
          resolve(false);
        }
      }, 100);
    });
  }

  /* ------------------------------------------------------------------ *
   * Mediaclip adapter                                                  *
   * ------------------------------------------------------------------ */
  var MediaclipAdapter = {
    id: 'mediaclip',
    _api: null,
    _initPromise: null,

    isReady: function () {
      return !!(window.mediaclipShopify && typeof window.mediaclipShopify.getCartApi === 'function');
    },

    // Resolve (and lazily init) the Mediaclip Cart API. We pass no sections and
    // a no-op onSectionsUpdated because the THEME owns re-rendering — we only use
    // the API to mutate + validate the cart, never to paint the DOM.
    ready: function () {
      var self = this;
      if (this._initPromise) return this._initPromise;
      this._initPromise = waitFor(function () { return self.isReady(); }).then(function (ok) {
        if (!ok) throw new Error('mediaclip api unavailable');
        self._api = window.mediaclipShopify.getCartApi();
        return Promise.resolve(self._api.init([], null, function () {})).then(function () {
          return self._api;
        });
      });
      // Don't cache a rejected init — let a later interaction retry.
      this._initPromise.catch(function () { self._initPromise = null; });
      return this._initPromise;
    },

    // Set the quantity of a main line and scale its sub-parts (extra sheets …).
    setMainQuantity: function (itemEl, quantity) {
      return this.ready().then(function (api) {
        var line = parseInt(itemEl.getAttribute('data-mc-shopify-cart-item-line'), 10);
        var items = api.getMainAndNonMediaclipItems();
        var target = items.filter(function (it) {
          return it.identifier && Number(it.identifier.line) === line;
        })[0];
        if (!target) throw new Error('mediaclip line ' + line + ' not found in cart api');
        api.setMainItemQuantity(target, quantity);
        return api.saveCart();
      });
    },

    // Returns { result: 'Ok' | 'Fixed' | 'Error', errors?: [...] }.
    validate: function () {
      return this.ready().then(function (api) {
        // skipRerender: the theme repaints the cart afterwards.
        return api.executeMediaclipValidationAndFixCart({ skipRerender: true });
      }).then(ignorePixfizzLineErrors);
    },

    refreshThumbnails: function () {
      return this.ready().then(function (api) {
        return api.refreshMediaclipProjectThumbnails();
      }).then(function (res) {
        if (!res || !res.thumbnailUrlsPerProjectId) return;
        Object.keys(res.thumbnailUrlsPerProjectId).forEach(function (projectId) {
          var url = res.thumbnailUrlsPerProjectId[projectId];
          if (!url) return;
          document
            .querySelectorAll('[data-mc-project-id="' + projectId + '"] .cart__item__image img')
            .forEach(function (img) { img.src = url; });
        });
      });
    },
  };

  /* Mediaclip validates every line of the cart. Products migrated to Pixfizz
   * are still associated with a Mediaclip product in Mediaclip Hub (removing
   * the association breaks their designer), so Mediaclip reports their lines
   * as "should-have-mediaclip-properties" and a cart mixing both providers
   * could never check out. Such an error is dropped when every cart line of
   * that variant is a Pixfizz line; any other error still blocks. */
  function isPixfizzLine(item) {
    var props = item.properties || {};
    return !!(props._pixfizz_project_id || props._pixfizz_addon);
  }

  function ignorePixfizzLineErrors(res) {
    if (!res || res.result !== 'Error' || !Array.isArray(res.errors)) return res;
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var pixfizzOnly = {};
        cart.items.forEach(function (item) {
          var id = String(item.variant_id);
          pixfizzOnly[id] = (pixfizzOnly[id] !== false) && isPixfizzLine(item);
        });
        var errors = res.errors.filter(function (err) {
          return !(err.problemId === 'should-have-mediaclip-properties' && pixfizzOnly[String(err.variantId)]);
        });
        if (errors.length === res.errors.length) return res;
        return errors.length ? { result: 'Error', errors: errors } : { result: 'Ok' };
      });
  }

  /* ------------------------------------------------------------------ *
   * Pixfizz adapter                                                    *
   * ------------------------------------------------------------------ */
  /* Follows Pixfizz's documented cart integration: the shopper keeps a quantity
   * control on a Pixfizz line, and every add-on line is scaled from it as
   * quantity * _pixfizz_unit_quantity. Their scheme, not one of ours.
   *
   * Pixfizz is client-only — its api.js talks straight to /cart/add.js and
   * /cart/update.js, and there is no server endpoint the cart form could post
   * to. So unlike Mediaclip there is NO backstop behind us: if the add-ons are
   * not kept in step here, the wrong quantities reach the order. Removal is
   * ours for the same reason — deleting a project must take its add-ons with it.
   *
   * Everything needed is on the line element (emitted by
   * snippets/cart-line-items-tipii.liquid):
   *   data-pixfizz-key     the main line's cart key
   *   data-pixfizz-addons  "key|unit;key|unit;" for its add-on lines
   */
  var PixfizzAdapter = {
    id: 'pixfizz',

    ready: function () {
      return Promise.resolve();
    },

    // main line -> quantity, each add-on line -> its per-unit count * quantity.
    _updatesFor: function (itemEl, quantity) {
      var updates = {};
      var key = itemEl.getAttribute('data-pixfizz-key');
      if (key) updates[key] = quantity;
      (itemEl.getAttribute('data-pixfizz-addons') || '').split(';').forEach(function (entry) {
        if (!entry) return;
        // A line-item key contains ':', so split on the LAST '|' only.
        var sep = entry.lastIndexOf('|');
        if (sep < 0) return;
        var unit = parseInt(entry.slice(sep + 1), 10);
        updates[entry.slice(0, sep)] = (isFinite(unit) ? unit : 1) * quantity;
      });
      return updates;
    },

    _update: function (updates) {
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      return fetch(root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: updates }),
      }).then(function (res) {
        if (!res.ok) throw new Error('pixfizz cart update failed: ' + res.status);
        return res;
      });
    },

    setMainQuantity: function (itemEl, quantity) {
      return this._update(this._updatesFor(itemEl, quantity));
    },

    // Removing the project must take its add-ons with it, or the shopper keeps
    // paying for extra pages of a book that is no longer in the cart.
    removeMain: function (itemEl) {
      return this._update(this._updatesFor(itemEl, 0));
    },

    // No server-side validation exists for Pixfizz -> a Pixfizz-only cart keeps
    // the theme's native checkout untouched (see validatingProviders()).
    validate: null,
  };

  var PROVIDERS = { mediaclip: MediaclipAdapter, pixfizz: PixfizzAdapter };

  /* ------------------------------------------------------------------ *
   * Generic controller                                                 *
   * ------------------------------------------------------------------ */
  function providerFor(itemEl) {
    var id = itemEl && itemEl.getAttribute('data-customization-provider');
    return id ? PROVIDERS[id] : null;
  }

  // Distinct provider adapters that currently have lines in the cart.
  function presentProviders() {
    var ids = {};
    document.querySelectorAll('.cart__item[data-customization-provider]').forEach(function (el) {
      ids[el.getAttribute('data-customization-provider')] = true;
    });
    return Object.keys(ids).map(function (id) { return PROVIDERS[id]; }).filter(Boolean);
  }

  // Providers present in the cart that actually have something to validate
  // before checkout. A provider without `validate` never intercepts the submit.
  function validatingProviders() {
    return presentProviders().filter(function (p) {
      return typeof p.validate === 'function';
    });
  }

  function checkoutButtons() {
    return document.querySelectorAll('[data-customization-checkout]');
  }

  function setBusy(busy) {
    checkoutButtons().forEach(function (btn) {
      btn.disabled = !!busy;
      btn.classList.toggle('loading', !!busy);
    });
  }

  function showMessage(form, text) {
    if (!form) return;
    var box = form.querySelector('[data-customization-message]');
    if (!box) {
      box = document.createElement('p');
      box.setAttribute('data-customization-message', '');
      box.className = 'cart__errors__content expanded';
      box.setAttribute('role', 'alert');
      box.style.margin = '8px 0';
      var anchor = form.querySelector('[data-cart-checkout-buttons]') || form.querySelector('.cart__buttons');
      if (anchor) anchor.parentNode.insertBefore(box, anchor);
      else form.appendChild(box);
    }
    box.textContent = text;
    box.classList.add('expanded');
  }

  function clearMessage(form) {
    if (!form) return;
    var box = form.querySelector('[data-customization-message]');
    if (box) box.textContent = '';
  }

  function refreshTheme() {
    document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
  }

  /* ---- 1. Quantity changes on customization main lines ---- *
   * Capture-phase on document so we run BEFORE the theme's per-item
   * `theme:cart:update` handler (which would change only this line via
   * /cart/change.js and leave the sub-parts behind). Bound once; survives
   * every cart re-render. */
  document.addEventListener(
    'theme:cart:update',
    function (event) {
      var origin = event.target;
      var main = origin && origin.closest ? origin.closest('.cart__item[data-customization-main]') : null;
      if (!main) return; // not a customization main line -> let the theme handle it
      var provider = providerFor(main);
      // Defensive: a provider that owned its own quantity would render no cart
      // control and implement no setMainQuantity — nothing to take over.
      if (!provider || typeof provider.setMainQuantity !== 'function') return;

      // Take over.
      event.stopImmediatePropagation();

      var quantity = parseInt(event.detail && event.detail.quantity, 10);
      if (!isFinite(quantity)) return;

      setBusy(true);
      provider
        .setMainQuantity(main, quantity)
        .catch(function (e) {
          // The Shopify cart may be in a partially-updated state; re-rendering
          // from the server reflects the source of truth either way.
          console.error('[customization-cart] quantity sync failed', e);
        })
        .then(function () {
          refreshTheme(); // theme repaints from the now-updated Shopify cart
          setBusy(false);
        });
    },
    true
  );

  /* ---- 1b. Removal of a customization main line ---- *
   * The theme binds its remove handler on the link itself, so a capture-phase
   * listener on document runs first and can take the line's grouped sub-lines
   * with it. Only adapters that implement `removeMain` opt in; everything else
   * (including Mediaclip today) keeps the theme's native removal. */
  document.addEventListener(
    'click',
    function (event) {
      var link = event.target && event.target.closest ? event.target.closest('[data-item-remove]') : null;
      if (!link) return;
      var main = link.closest('.cart__item[data-customization-main]');
      if (!main) return;
      var provider = providerFor(main);
      if (!provider || typeof provider.removeMain !== 'function') return;

      event.preventDefault();
      event.stopImmediatePropagation();

      setBusy(true);
      provider
        .removeMain(main)
        .catch(function (e) {
          console.error('[customization-cart] removal failed', e);
        })
        .then(function () {
          refreshTheme();
          setBusy(false);
        });
    },
    true
  );

  /* form.submit() drops the clicked button's name, and Shopify's /cart only
   * goes to checkout when a "checkout" field is posted. Mediaclip's endpoint
   * redirects to checkout on its own, so this matters for carts posting to
   * /cart (mixed Mediaclip + Pixfizz carts, see sections/cart*.liquid). */
  function submitToCheckout(form) {
    if (!form.querySelector('input[name="checkout"]')) {
      var field = document.createElement('input');
      field.type = 'hidden';
      field.name = 'checkout';
      form.appendChild(field);
    }
    form.submit();
  }

  /* ---- 2. Checkout validation ---- *
   * Capture-phase click so we run before the theme's submit/checkout handlers. */
  var validated = false;
  document.addEventListener(
    'click',
    function (event) {
      var btn = event.target && event.target.closest ? event.target.closest('[data-customization-checkout]') : null;
      if (!btn) return;
      if (validated) return; // already validated this attempt -> allow native submit

      var providers = validatingProviders();
      if (!providers.length) return; // nothing to validate -> native checkout

      var form = btn.form || (btn.closest ? btn.closest('form') : null);

      // Honour the theme's "accept terms" gate ourselves, since we're about to
      // intercept the submit the theme would otherwise validate.
      var terms = form && form.querySelector('[data-cart-acceptance-checkbox]');
      if (terms && !terms.checked) {
        event.preventDefault();
        event.stopImmediatePropagation();
        var termsErr = form.querySelector('[data-terms-error-message]');
        if (termsErr) {
          termsErr.textContent =
            (window.theme && window.theme.strings && window.theme.strings.cartAcceptanceError) || '';
          termsErr.classList.add('expanded');
        }
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      clearMessage(form);
      setBusy(true);

      // Run each present provider's validation in sequence.
      var chain = Promise.resolve({ fixed: false });
      providers.forEach(function (provider) {
        chain = chain.then(function (state) {
          if (state.stop) return state; // already decided (error)
          return provider.validate().then(function (res) {
            if (!res) return state;
            if (res.result === 'Error') {
              return { stop: true, error: res };
            }
            if (res.result === 'Fixed') state.fixed = true;
            return state;
          });
        });
      });

      chain
        .then(function (state) {
          if (state.error) {
            showMessage(
              form,
              'Une erreur est survenue lors de la validation du panier. Veuillez réessayer ou contacter le support.'
            );
            setBusy(false);
            return;
          }
          if (state.fixed) {
            showMessage(form, 'Votre panier a été mis à jour. Veuillez réessayer.');
            refreshTheme();
            setBusy(false);
            return;
          }
          // All good -> let it through (native submit bypasses these listeners).
          validated = true;
          if (form) submitToCheckout(form);
        })
        .catch(function (e) {
          // Fail open to the server-side backstop: the form posts to the
          // provider's handle-cart-form endpoint which re-validates.
          console.error('[customization-cart] validation failed', e);
          validated = true;
          if (form) submitToCheckout(form);
          else setBusy(false);
        });
    },
    true
  );

  /* ---- 3. Warm up + refresh thumbnails on load and when the drawer opens ---- */
  function boot() {
    presentProviders().forEach(function (p) {
      if (p.refreshThumbnails) p.refreshThumbnails().catch(function () {});
    });
  }

  /* ---- 4. Required add-ons ---- *
   * A line flagged by snippets/cart-line-items-tipii.liquid
   * ([data-required-addon]) belongs to a product that names a variant to offer
   * with it (`custom.required_addon`), and that variant is not in the cart.
   * Add it once per project, as a plain line: the shopper may then change its
   * quantity or remove it, and a removed add-on is not added back. "Once" is
   * remembered in localStorage, so it survives the redirects from the editor,
   * and only after Shopify accepted the add: a failed add (sold out, product
   * not yet on the Online Store) is retried on the next page load.
   * The cart re-renders in many places (page, drawer, AJAX sections), so a
   * MutationObserver catches every new render instead of one theme event. */
  var OFFERED_KEY = 'tipii_required_addon_offered';

  function offeredProjects() {
    try {
      return JSON.parse(localStorage.getItem(OFFERED_KEY) || '{}') || {};
    } catch (e) {
      return {};
    }
  }

  // Requested during this page load, whatever the outcome: no retry loop.
  var requestedInPage = {};

  function markOffered(project) {
    try {
      var offered = offeredProjects();
      offered[project] = true;
      localStorage.setItem(OFFERED_KEY, JSON.stringify(offered));
    } catch (e) {}
  }

  function addMissingRequiredAddons() {
    var offered = offeredProjects();
    var quantities = {};
    var projects = [];
    document.querySelectorAll('.cart__item[data-required-addon]').forEach(function (el) {
      var variantId = el.getAttribute('data-required-addon');
      var project = el.getAttribute('data-required-addon-project');
      if (!variantId || !project || offered[project] || requestedInPage[project]) return;
      requestedInPage[project] = true;
      projects.push(project);
      quantities[variantId] = 1;
    });
    var items = Object.keys(quantities).map(function (id) {
      return { id: Number(id), quantity: 1 };
    });
    if (!items.length) return;

    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items }),
    })
      .then(function (res) {
        if (!res.ok) throw new Error('required add-on not added: ' + res.status);
        projects.forEach(markOffered);
        refreshTheme();
      })
      .catch(function (e) {
        console.error('[customization-cart] required add-on', e);
      });
  }

  var requiredAddonsTimer = null;
  new MutationObserver(function () {
    clearTimeout(requiredAddonsTimer);
    requiredAddonsTimer = setTimeout(addMissingRequiredAddons, 100);
  }).observe(document.documentElement, { childList: true, subtree: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('theme:cart-drawer:open', boot);
  addMissingRequiredAddons();
})();
